import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react';
import {
  acknowledgeDocuments,
  fetchCopyText,
  fetchFailurePage,
  fetchIgnoredPage,
  fetchNotProcessed,
  tryAgain,
  unignoreDocuments,
  type FailureEntryView,
  type FailureGroupSummary,
  type IgnoredEntryView,
  type NotProcessedSummary,
} from '../lib/api';
import { useActionError } from '../lib/action-error';
import { ask } from '../lib/ask';
import { DocId } from '../lib/doc-id';
import { formatShortDate } from '../lib/format-date';
import { usePoll } from '../lib/use-poll';
import { Pager, usePagedList } from '../lib/use-paged-list';
import { LoadError } from '../lib/load-error';
import { InfoTip, useTip } from '../lib/info-tip';
import { documentTypeLabel, failureClassHeading, labels } from '../lib/labels';
import { RoleNote, useGate } from '../lib/session';
import { SourceFileViewer } from '../lib/source-file-viewer';

type LoadState = { status: 'loading' } | { status: 'error' } | { status: 'ok'; view: NotProcessedSummary };

/** QA-163: one file viewer is open on the page at a time, keyed `<table>:<failureClass>:<docId>`. */
const OpenFileContext = createContext<{ open: string | null; toggle: (key: string) => void }>({
  open: null,
  toggle: () => undefined,
});

/**
 * `#not-processed`: the four `failureClass` groups plus the
 * Ignored group, one table per class — not a chart, per the row's Notes. The headings come from one
 * summary read; each table reads its own page of 20 (QA-85), and only page 1 polls. Copy is a read and never
 * guarded; *Try again* and *Acknowledge* are guarded (`lib/ask.tsx`, this console's first
 * guarded writes — no modal library exists yet to reach for instead); Un-ignore reverses
 * Acknowledge and carries the same guard.
 */
export function NotProcessed({ failureClass }: { failureClass?: string }) {
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [openFile, setOpenFile] = useState<string | null>(null);
  const fileContext = useMemo(
    () => ({ open: openFile, toggle: (key: string) => setOpenFile((prev) => (prev === key ? null : key)) }),
    [openFile],
  );

  // Keep the sections mounted while the data reloads, so a button under the pointer stays in place.
  function reload() {
    setState((prev) => (prev.status === 'ok' ? prev : { status: 'loading' }));
    refresh();
  }

  // The background refresh: no loading state, and a failure keeps the last data on the screen.
  function refresh() {
    fetchNotProcessed()
      .then((view) => setState({ status: 'ok', view }))
      .catch(() => setState((prev) => (prev.status === 'ok' ? prev : { status: 'error' })));
  }

  useEffect(reload, []);
  usePoll(refresh);

  // QA-101: the tables above the target group load after it takes focus, and each one grows by up to
  // 20 rows, so focus alone leaves the group below the fold. Scroll one time, when every table above
  // it has settled, and only if the operator has not moved on. A poll does not scroll again.
  const [settled, setSettled] = useState<ReadonlySet<string>>(new Set());
  const markSettled = useCallback(
    (settledClass: string) => setSettled((prev) => (prev.has(settledClass) ? prev : new Set(prev).add(settledClass))),
    [],
  );
  const scrolled = useRef(false);
  useEffect(() => {
    scrolled.current = false;
  }, [failureClass]);
  useEffect(() => {
    if (scrolled.current || !failureClass || state.status !== 'ok') return;
    const { groups } = state.view;
    const target = groups.findIndex((group) => group.failureClass === failureClass);
    if (target < 0 || !groups.slice(0, target).every((g) => g.count === 0 || settled.has(g.failureClass))) return;
    scrolled.current = true;
    const heading = document.getElementById(`failure-class-${failureClass}`)?.querySelector('h3');
    if (heading && heading === document.activeElement) heading.scrollIntoView({ block: 'start' });
  }, [failureClass, state, settled]);

  return (
    <div>
      <div className="page-head">
        <h2>{labels.pageTitles.notProcessed}</h2>
      </div>
      {state.status === 'loading' && <p className="notice">{labels.notProcessed.loading}</p>}
      {state.status === 'error' && <LoadError text={labels.notProcessed.loadFailed} onRetry={reload} />}
      {state.status === 'ok' && (
        <OpenFileContext.Provider value={fileContext}>
          {failureClass && state.view.groups.some((group) => group.failureClass === failureClass) && (
            <p className="notice notice--good" role="status">
              {labels.notProcessed.routeContext(failureClassHeading(failureClass))}
            </p>
          )}
          {state.view.groups.map((group) => (
            <FailureGroupSection
              key={group.failureClass}
              group={group}
              onChanged={reload}
              onSettled={markSettled}
              isContextTarget={group.failureClass === failureClass}
            />
          ))}
          <IgnoredSection ignored={state.view.ignored} onChanged={reload} />
        </OpenFileContext.Provider>
      )}
    </div>
  );
}

function FailureGroupSection({
  group,
  onChanged,
  onSettled,
  isContextTarget,
}: {
  group: FailureGroupSummary;
  onChanged: () => void;
  onSettled: (failureClass: string) => void;
  isContextTarget: boolean;
}) {
  const [copyText, setCopyText] = useState('');
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const [showCopiedList, setShowCopiedList] = useState(true);
  const groupHeadingRef = useRef<HTMLHeadingElement>(null);
  // QA-134: the copy button carries its own tooltip; a click runs the copy at once, never a pin.
  const copyTip = useTip(labels.notProcessed.copyTip(group.count, group.isFloor), false);

  useEffect(() => {
    if (isContextTarget) groupHeadingRef.current?.focus();
  }, [isContextTarget]);

  // QA-85: the whole group, read now, not on each poll.
  async function handleCopy() {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable');
      const text = await fetchCopyText(group.failureClass);
      await navigator.clipboard.writeText(text);
      setCopyText(text);
      setCopyState('copied');
      setShowCopiedList(true);
    } catch {
      setCopyState('failed');
    }
  }

  const empty = group.count === 0;
  const copyLabel = labels.notProcessed.copyList[group.failureClass] ?? labels.notProcessed.copyListFallback;
  return (
    <section className={`panel${empty ? ' panel--empty' : ''}`} id={`failure-class-${group.failureClass}`}>
      <div className="panel__head">
        <h3 ref={groupHeadingRef} tabIndex={-1}>
          {failureClassHeading(group.failureClass)} · {group.isFloor ? `≥ ${group.count}` : group.count}
        </h3>
        {/* An empty group is one line: its heading and a note, with no action to offer. */}
        {empty && <span className="panel__note">{labels.notProcessed.emptyGroup}</span>}
        {/* QA-84: Copy is a read and is never guarded (this screen's own rule), so it stays quiet. */}
        {!empty && (
          <div className="action-row end-edge">
            <button
              type="button"
              className="btn"
              {...copyTip.buttonProps}
              onClick={() => {
                copyTip.hide();
                void handleCopy();
              }}
            >
              {copyLabel}
            </button>
            {copyTip.bubble}
          </div>
        )}
      </div>
      {/* One line of what-to-do per group: the operator needs the next action, not the taxonomy. */}
      {!empty && <p className="panel__sub">{labels.notProcessed.classWhatToDo[group.failureClass]}</p>}
      {group.isFloor && <p className="notice notice--info">{labels.notProcessed.isFloorNote}</p>}
      {copyState === 'copied' && (
        <>
          <p className="notice notice--snackbar" role="status">
            {labels.notProcessed.copied}
          </p>
          <div className="copy-preview">
            <button
              type="button"
              className="btn btn--text"
              aria-expanded={showCopiedList}
              onClick={() => setShowCopiedList((visible) => !visible)}
            >
              {showCopiedList ? labels.notProcessed.hideCopiedList : labels.notProcessed.showCopiedList}
            </button>
            {showCopiedList && <pre>{copyText}</pre>}
          </div>
        </>
      )}
      {copyState === 'failed' && (
        <p className="notice notice--crit" role="status">
          {labels.notProcessed.copyFailed}
        </p>
      )}
      {!empty && (
        <FailureTable
          failureClass={group.failureClass}
          onChanged={onChanged}
          onSettled={onSettled}
          focusTarget={groupHeadingRef}
        />
      )}
    </section>
  );
}

/** One page of one group. The selection is the rows of this page: it clears on a page change. */
function FailureTable({
  failureClass,
  onChanged,
  onSettled,
  focusTarget,
}: {
  failureClass: FailureGroupSummary['failureClass'];
  onChanged: () => void;
  onSettled: (failureClass: string) => void;
  focusTarget: RefObject<HTMLElement | null>;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const action = useActionError();
  const gate = useGate('administer');
  const [sentAgain, setSentAgain] = useState(false);
  const list = usePagedList<FailureEntryView>(
    (cursor, signal) =>
      fetchFailurePage(failureClass, cursor, signal).then(({ entries, nextCursor }) => ({
        items: entries,
        nextCursor,
      })),
    0,
    true,
    focusTarget,
  );
  const { page } = list;

  // The first page or the first error: `NotProcessed` scrolls to its target group after this.
  useEffect(() => {
    if (page || list.failed) onSettled(failureClass);
  }, [page, list.failed, failureClass, onSettled]);

  useEffect(() => setSelected(new Set()), [list.cursor]);

  function toggle(docId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  }

  // A row that left the page on a poll is no longer selected.
  const onPage = new Set(page?.items.map((entry) => entry.docId));
  const chosen = [...selected].filter((docId) => onPage.has(docId));
  // QA-140: Try again always fails on these two groups (an unknown folder and a duplicate filename).
  // QA-158: and while a chosen row is held by review. This is fine, ignore it stays.
  const chosenHeld = page?.items.some((entry) => entry.heldBy && selected.has(entry.docId)) ?? false;
  const canTryAgain = failureClass !== 'unroutable' && failureClass !== 'collision' && !chosenHeld;

  function changed() {
    list.reload();
    onChanged();
  }

  async function handleTryAgain() {
    const docIds = chosen;
    if (docIds.length === 0) return;
    const answers = await ask({
      ...labels.notProcessed.tryAgainConfirm(docIds.length, failureClass),
      confirmLabel: labels.notProcessed.tryAgain,
    });
    if (!answers) return;
    setSentAgain(false);
    if (!(await action.run(() => tryAgain(docIds)))) return;
    setSelected(new Set());
    setSentAgain(true);
    changed();
  }

  async function handleAcknowledge() {
    const docIds = chosen;
    if (docIds.length === 0) return;
    const answers = await ask({
      ...labels.notProcessed.acknowledgeConfirm(docIds.length),
      confirmLabel: labels.notProcessed.acknowledge,
      tone: 'danger',
    });
    if (!answers) return;
    if (!(await action.run(() => acknowledgeDocuments(docIds)))) return;
    setSelected(new Set());
    changed();
  }

  return (
    <>
      {/* A viewer reads the list; the row checkboxes and the two group actions are for an admin. */}
      <RoleNote action="administer" />
      {list.failed && !page && <LoadError text={labels.notProcessed.loadFailed} onRetry={list.retry} />}
      {!list.failed && !page && <p className="notice">{labels.notProcessed.loading}</p>}
      {page && page.items.length === 0 && !list.first && <p className="notice">{labels.paging.noOlder}</p>}
      {page && page.items.length > 0 && (
        <div className="responsive-table flow-md">
          <table className="grid-failures">
            <thead>
              <tr>
                <th aria-label={labels.notProcessed.selectAll} />
                <th>{labels.notProcessed.columns.document}</th>
                <th>{labels.notProcessed.columns.filed}</th>
                <th>
                  {failureClass === 'misfiled'
                    ? labels.notProcessed.columns.detected
                    : labels.notProcessed.columns.reason}
                </th>
                <th>{labels.notProcessed.columns.since}</th>
                <th className="num">{labels.notProcessed.columns.attempt}</th>
                <th className="table-actions">
                  <span className="visually-hidden">{labels.tableActions}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {page.items.map((entry) => (
                <EntryRow
                  key={entry.docId}
                  failureClass={failureClass}
                  entry={entry}
                  selected={selected.has(entry.docId)}
                  onToggle={toggle}
                  canSelect={gate.allowed}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pager list={list} focusTarget={focusTarget} />
      {action.notice}
      {sentAgain && (
        <p className="notice notice--good" role="status">
          {labels.notProcessed.sentAgain}
        </p>
      )}
      {/* QA-79: the two group actions show only while a row is checked, so no greyed-out
          control waits on the page. Both are guarded writes (`lib/ask.tsx`), so both wear the
          guard fill; Try again is first because it is the one that fixes rather than files, and it
          shows only on the groups where it can work (QA-140). */}
      {chosen.length > 0 && (
        <div className="action-row flow-md">
          {canTryAgain && (
            <button type="button" className="btn btn--guard" onClick={handleTryAgain} {...gate.props}>
              {labels.notProcessed.tryAgain} · {chosen.length}
            </button>
          )}
          <button type="button" className="btn btn--guard btn--danger" onClick={handleAcknowledge} {...gate.props}>
            {labels.notProcessed.acknowledge} · {chosen.length}
          </button>
        </div>
      )}
    </>
  );
}

/** The columns of a group table and of the Ignored table, for the viewer row that spans them. */
const FAILURE_COLUMNS = 7;
const IGNORED_COLUMNS = 5;

/** QA-163: **Show the file** on a row, and whether its viewer row is open. One is open on the page at a time. */
function useRowFile(key: string) {
  const files = useContext(OpenFileContext);
  const viewerId = useId();
  const open = files.open === key;
  return {
    open,
    viewerId,
    button: (
      <button
        type="button"
        className="btn"
        aria-expanded={open}
        aria-controls={open ? viewerId : undefined}
        onClick={() => files.toggle(key)}
      >
        {open ? labels.sourceFile.hide : labels.sourceFile.show}
      </button>
    ),
  };
}

/** The viewer opens under its row, across every column. */
function FileViewerRow({ id, docId, columns }: { id: string; docId: string; columns: number }) {
  return (
    <tr className="file-viewer-row" id={id}>
      <td colSpan={columns}>
        <SourceFileViewer docId={docId} />
      </td>
    </tr>
  );
}

function EntryRow({
  failureClass,
  entry,
  selected,
  onToggle,
  canSelect,
}: {
  failureClass: string;
  entry: FailureEntryView;
  selected: boolean;
  onToggle: (docId: string) => void;
  canSelect: boolean;
}) {
  const file = useRowFile(`failure:${failureClass}:${entry.docId}`);
  return (
    <>
      <tr>
        <td>
          <input
            type="checkbox"
            disabled={entry.retrying || !canSelect}
            checked={selected}
            onChange={() => onToggle(entry.docId)}
            aria-label={entry.docId}
          />
        </td>
        <td>
          <a className="mono" href={`#doc/${entry.docId}`} aria-label={entry.docId}>
            <DocId id={entry.docId} />
          </a>
        </td>
        <td>{documentTypeLabel(entry.filedType)}</td>
        <td>
          {entry.retrying && <strong>{labels.notProcessed.tryingAgain} </strong>}
          {entry.heldBy && (
            <>
              <strong>{labels.notProcessed.held[entry.heldBy].line}</strong>
              <InfoTip label={entry.docId} text={labels.notProcessed.held[entry.heldBy].tip} />{' '}
            </>
          )}
          {entry.detectedType
            ? documentTypeLabel(entry.detectedType)
            : (entry.claimedKey ??
              (entry.step ? `${entry.step} · ${entry.errorClass ?? labels.notProcessed.unknownError}` : entry.error) ??
              '—')}
        </td>
        <td className="mono">{formatShortDate(entry.timestamp)}</td>
        <td className="mono num">{entry.attempt}</td>
        <td className="table-actions">{file.button}</td>
      </tr>
      {file.open && <FileViewerRow id={file.viewerId} docId={entry.docId} columns={FAILURE_COLUMNS} />}
    </>
  );
}

function IgnoredSection({ ignored, onChanged }: { ignored: NotProcessedSummary['ignored']; onChanged: () => void }) {
  const empty = ignored.count === 0;
  const headingRef = useRef<HTMLHeadingElement>(null);
  return (
    <section className={`panel${empty ? ' panel--empty' : ''}`}>
      <div className="panel__head">
        <h3 ref={headingRef} tabIndex={-1}>
          {labels.notProcessed.ignoredHeading} · {ignored.isFloor ? `≥ ${ignored.count}` : ignored.count}
        </h3>
        <InfoTip label={labels.notProcessed.ignoredHeading} text={labels.notProcessed.ignoredTip} />
        {empty && <span className="panel__note">{labels.notProcessed.emptyGroup}</span>}
      </div>
      {ignored.isFloor && <p className="notice notice--info">{labels.notProcessed.isFloorNote}</p>}
      {!empty && <IgnoredTable onChanged={onChanged} focusTarget={headingRef} />}
    </section>
  );
}

function IgnoredTable({
  onChanged,
  focusTarget,
}: {
  onChanged: () => void;
  focusTarget: RefObject<HTMLElement | null>;
}) {
  const action = useActionError();
  const gate = useGate('administer');
  const list = usePagedList<IgnoredEntryView>(
    (cursor, signal) =>
      fetchIgnoredPage(cursor, signal).then(({ entries, nextCursor }) => ({ items: entries, nextCursor })),
    0,
    true,
    focusTarget,
  );
  const { page } = list;

  async function handleUnignore(entry: IgnoredEntryView) {
    const answers = await ask({
      ...labels.notProcessed.unignoreConfirm(entry.docId),
      confirmLabel: labels.notProcessed.unignore,
    });
    if (!answers) return;
    if (await action.run(() => unignoreDocuments([entry.docId]))) {
      list.reload();
      onChanged();
    }
  }

  return (
    <>
      <RoleNote action="administer" />
      {action.notice}
      {list.failed && !page && <LoadError text={labels.notProcessed.loadFailed} onRetry={list.retry} />}
      {!list.failed && !page && <p className="notice">{labels.notProcessed.loading}</p>}
      {page && page.items.length === 0 && !list.first && <p className="notice">{labels.paging.noOlder}</p>}
      {page && page.items.length > 0 && (
        <div className="responsive-table">
          <table>
            <thead>
              <tr>
                <th>{labels.notProcessed.columns.document}</th>
                <th>{labels.notProcessed.columns.class}</th>
                <th>{labels.notProcessed.columns.acknowledgedBy}</th>
                <th>{labels.notProcessed.columns.acknowledgedAt}</th>
                <th className="table-actions">
                  <span className="visually-hidden">{labels.tableActions}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {page.items.map((entry) => (
                <IgnoredRow
                  key={`${entry.failureClass}/${entry.docId}`}
                  entry={entry}
                  onUnignore={handleUnignore}
                  gateProps={gate.props}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pager list={list} focusTarget={focusTarget} />
    </>
  );
}

function IgnoredRow({
  entry,
  onUnignore,
  gateProps,
}: {
  entry: IgnoredEntryView;
  onUnignore: (entry: IgnoredEntryView) => void;
  gateProps: ReturnType<typeof useGate>['props'];
}) {
  const file = useRowFile(`ignored:${entry.failureClass}:${entry.docId}`);
  return (
    <>
      <tr>
        <td>
          <a className="mono" href={`#doc/${entry.docId}`} aria-label={entry.docId}>
            <DocId id={entry.docId} />
          </a>
        </td>
        <td>{failureClassHeading(entry.failureClass)}</td>
        <td className="wrap-anywhere">{entry.acknowledgedBy}</td>
        <td className="mono">{formatShortDate(entry.acknowledgedAt)}</td>
        <td className="table-actions">
          <div className="action-row">
            {/* The read first, then Un-ignore. Both quiet, not guard-filled: eleven of them in one
                column would turn it orange. The guard is the dialog that opens (`design.md` § CTA voice). */}
            {file.button}
            <button type="button" className="btn" onClick={() => onUnignore(entry)} {...gateProps}>
              {labels.notProcessed.unignore}
            </button>
          </div>
        </td>
      </tr>
      {file.open && <FileViewerRow id={file.viewerId} docId={entry.docId} columns={IGNORED_COLUMNS} />}
    </>
  );
}
